import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from "@shadcn";
import { cn } from "@utils/design-utils";
import { Fragment } from "react";

type BreadcrumbEntry = {
  label: string;
  onClick?: () => void;
  href?: string;
};

type PageBreadcrumbsProps = {
  items: BreadcrumbEntry[];
  className?: string;
};

export const PageBreadcrumbs = ({
  items,
  className,
}: PageBreadcrumbsProps): JSX.Element | null => {
  if (!items || items.length === 0) {
    return null;
  }

  const maxVisible = 4;
  const shouldCollapse = items.length > maxVisible;

  const buildItems = () => {
    if (!shouldCollapse) {
      return items;
    }

    const [first, second, ...restItems] = items;
    const lastTwo = items.slice(-2);
    const ellipsisSlot = { label: "ellipsis" } as BreadcrumbEntry;

    if (items.length <= 5) {
      return [first, second, ellipsisSlot, ...items.slice(-2)];
    }

    return [first, second, ellipsisSlot, ...lastTwo];
  };

  const displayItems = buildItems();

  return (
    <Breadcrumb className={cn("mb-6 pt-1.5", className)}>
      <BreadcrumbList>
        {displayItems.map((item, index) => {
          const isEllipsis = item.label === "ellipsis";
          const isCurrent = !isEllipsis && index === displayItems.length - 1;

          return (
            <Fragment key={`${item.label}-${index}`}>
              {index > 0 ? <BreadcrumbSeparator /> : null}
              <BreadcrumbItem>
                {isEllipsis ? (
                  <BreadcrumbEllipsis />
                ) : isCurrent ? (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                ) : item.onClick ? (
                  <BreadcrumbLink asChild>
                    <button
                      type="button"
                      onClick={item.onClick}
                      className="text-left leading-none"
                    >
                      {item.label}
                    </button>
                  </BreadcrumbLink>
                ) : item.href ? (
                  <BreadcrumbLink href={item.href} className="leading-none">
                    {item.label}
                  </BreadcrumbLink>
                ) : (
                  <span className="text-sm text-neutral-dark leading-none">{item.label}</span>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
